/**
 * Problem-section illustrations — self-contained port of the three procedural
 * isometric SVG generators (FigDelayedSignal, FigScatteredKnowledge,
 * FigLaunchRace). Each builder returns { vb, paths, ellipses, dots };
 * renderFigure() draws it into a `.ao-prob-vis[data-fig]` container. Keyframes
 * and CSS custom properties live in main.scss.
 *
 * Original design-tool prototypes are preserved for reference (not used at
 * runtime) in `src/statics/figures/*.dc.html` — copied to `dist/figures/` by
 * the build's CopyWebpackPlugin.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const C = 0.8660254, S = 0.5;

/* ── shared geometry helpers ─────────────────────────────────────────────── */
const iso = (x, y, z) => [(x - y) * C, (x + y) * S - z];

const P = (pts, close) =>
    'M ' + pts.map((p) => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' L ') + (close ? ' Z' : '');

const RP = (pts, r) => {
    let d = '';
    const n = pts.length;
    for (let i = 0; i < n; i++) {
        const p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n];
        const v1 = [p1[0] - p0[0], p1[1] - p0[1]], l1 = Math.hypot(v1[0], v1[1]) || 1;
        const v2 = [p2[0] - p1[0], p2[1] - p1[1]], l2 = Math.hypot(v2[0], v2[1]) || 1;
        const r1 = Math.min(r, l1 / 2), r2 = Math.min(r, l2 / 2);
        const a = [p1[0] - v1[0] / l1 * r1, p1[1] - v1[1] / l1 * r1];
        const b = [p1[0] + v2[0] / l2 * r2, p1[1] + v2[1] / l2 * r2];
        d += (i === 0 ? 'M ' + a[0].toFixed(1) + ' ' + a[1].toFixed(1) : ' L ' + a[0].toFixed(1) + ' ' + a[1].toFixed(1));
        d += ' Q ' + p1[0].toFixed(1) + ' ' + p1[1].toFixed(1) + ' ' + b[0].toFixed(1) + ' ' + b[1].toFixed(1);
    }
    return d + ' Z';
};

const fit = (raw, box) => {
    let a = 1e9, b = 1e9, c = -1e9, d = -1e9;
    raw.forEach((p) => { a = Math.min(a, p[0]); b = Math.min(b, p[1]); c = Math.max(c, p[0]); d = Math.max(d, p[1]); });
    const w = c - a, h = d - b, bw = box[2] - box[0], bh = box[3] - box[1];
    const sc = Math.min(bw / w, bh / h);
    const ox = box[0] + (bw - w * sc) / 2 - a * sc, oy = box[1] + (bh - h * sc) / 2 - b * sc;
    return { sc, tx: (p) => [p[0] * sc + ox, p[1] * sc + oy], rect: [a * sc + ox, b * sc + oy, w * sc, h * sc] };
};

const vbFrom = (f, p) => {
    const r = f.rect;
    return (r[0] - p).toFixed(1) + ' ' + (r[1] - p).toFixed(1) + ' ' + (r[2] + 2 * p).toFixed(1) + ' ' + (r[3] + 2 * p).toFixed(1);
};

const boxFn = (cx, cy, hx, hy, H) => {
    const t = [iso(cx - hx, cy - hy, H), iso(cx + hx, cy - hy, H), iso(cx + hx, cy + hy, H), iso(cx - hx, cy + hy, H)];
    const bl = iso(cx - hx, cy + hy, 0), bf = iso(cx + hx, cy + hy, 0), br = iso(cx + hx, cy - hy, 0);
    return { t, bl, bf, br };
};

/* ── Fig 01 · Delayed signal ─────────────────────────────────────────────── */
function buildDelayedSignal() {
    const primary = (w) => ({ fill: 'none', stroke: 'var(--line)', strokeOpacity: .62, strokeWidth: w || 'var(--sw)', strokeLinejoin: 'round', strokeLinecap: 'round' });
    const secondary = (w) => ({ fill: 'none', stroke: 'var(--sec)', strokeOpacity: .40, strokeWidth: w || 'calc(var(--sw) * 0.72)', strokeLinejoin: 'round', strokeLinecap: 'round' });
    const cons = (w) => ({ fill: 'none', stroke: 'var(--cons)', strokeOpacity: .34, strokeWidth: w || 3.2, strokeLinejoin: 'round', strokeLinecap: 'round' });

    const H = 250, gap = 148, th = 22, nz = 5, R = Math.SQRT2;
    const zc = []; for (let i = 0; i < nz; i++) zc.push(i * gap);
    const planes = zc.map((z) => ({ top: [iso(-H, -H, z), iso(H, -H, z), iso(H, H, z), iso(-H, H, z)], bot: [iso(H, -H, z - th), iso(H, H, z - th), iso(-H, H, z - th)], z }));
    const raw = []; planes.forEach((p) => { raw.push(...p.top, ...p.bot); });
    const f = fit(raw, [90, 75, 910, 925]), tx = f.tx, sc = f.sc;
    const paths = [], dots = [], ell = [];
    planes.forEach((p) => { const top = p.top.map(tx), b = p.bot.map(tx); paths.push({ d: P(top, true), style: primary() }); paths.push({ d: P([top[1], b[0], b[1], b[2], top[3]]), style: secondary() }); });
    paths.push({ d: P([tx(iso(0, 0, zc[nz - 1])), tx(iso(0, 0, 0))]), style: Object.assign(secondary(.9), { strokeOpacity: .3, strokeDasharray: '1 6' }) });
    [1, 3].forEach((ci) => paths.push({ d: P([tx(planes[nz - 1].top[ci]), tx(planes[0].top[ci])]), style: Object.assign(cons(), { strokeDasharray: '1 7', animation: 'guide 8s var(--e) infinite' }) }));
    const topC = tx(iso(0, 0, zc[nz - 1]));
    [55, 115, 180, 250].forEach((r, k) => ell.push({ cx: topC[0].toFixed(1), cy: topC[1].toFixed(1), rx: (r * C * R * sc).toFixed(1), ry: (r * S * R * sc).toFixed(1), style: { fill: 'none', stroke: 'var(--active)', strokeOpacity: .65, strokeWidth: 3.6, transformBox: 'fill-box', transformOrigin: '50% 50%', animation: `radar 8s var(--e) ${(-8 + 0.3 + 0.32 * k).toFixed(2)}s infinite` } }));
    const T = [1.2, 2.2, 3.1, 4.0, 4.9];
    for (let i = 0; i < nz; i++) { const c = tx(iso(0, 0, zc[nz - 1 - i])); dots.push({ cx: c[0].toFixed(1), cy: c[1].toFixed(1), r: 5.4, style: { fill: 'var(--active)', filter: 'url(#glow1)', opacity: .78, animation: `node 8s var(--e) ${(T[i] - 12).toFixed(2)}s infinite` } }); }
    const bc = tx(iso(0, 0, 0));
    ell.push({ cx: bc[0].toFixed(1), cy: bc[1].toFixed(1), rx: (66 * C * R * sc).toFixed(1), ry: (66 * S * R * sc).toFixed(1), style: { fill: 'none', stroke: 'var(--active)', strokeOpacity: .7, strokeWidth: 'calc(var(--sw) * 1.15)', transformBox: 'fill-box', transformOrigin: '50% 50%', animation: 'pulseRing 8s var(--e) -8s infinite' } });
    ell.push({ cx: bc[0].toFixed(1), cy: bc[1].toFixed(1), rx: (108 * C * R * sc).toFixed(1), ry: (108 * S * R * sc).toFixed(1), style: Object.assign(cons(), { strokeDasharray: '1 6' }) });
    dots.push({ cx: bc[0].toFixed(1), cy: bc[1].toFixed(1), r: 5.4, style: { fill: 'var(--active)', filter: 'url(#glow1)', opacity: .78, animation: `node 8s var(--e) ${(5.6 - 12).toFixed(2)}s infinite` } });
    return { vb: vbFrom(f, 30), paths, ellipses: ell, dots, defs: '<filter id="glow1" x="-300%" y="-300%" width="700%" height="700%"><feGaussianBlur stdDeviation="3.4" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>' };
}

/* ── Fig 02 · Scattered knowledge ────────────────────────────────────────── */
function buildScattered() {
    const primary = (w) => ({ fill: 'none', stroke: 'var(--line)', strokeOpacity: .62, strokeWidth: w || 'var(--sw)', strokeLinejoin: 'round', strokeLinecap: 'round' });
    const secondary = (w) => ({ fill: 'none', stroke: 'var(--sec)', strokeOpacity: .40, strokeWidth: w || 'calc(var(--sw) * 0.72)', strokeLinejoin: 'round', strokeLinecap: 'round' });
    const cons = (w) => ({ fill: 'none', stroke: 'var(--cons)', strokeOpacity: .34, strokeWidth: w || 3.2, strokeLinejoin: 'round', strokeLinecap: 'round' });

    const cubes = [
        { x: -60, y: -280, hf: 150, H: 300, phase: 1.0, top: 'dots' },
        { x: -320, y: 30, hf: 145, H: 285, phase: 2.7, top: 'dots' },
        { x: 300, y: -120, hf: 140, H: 175, phase: 4.3, top: 'dots' },
        { x: 30, y: 250, hf: 150, H: 210, phase: 6.4, top: 'dots' },
    ];
    const raw = [];
    cubes.forEach((c) => { const b = boxFn(c.x, c.y, c.hf, c.hf, c.H); raw.push(...b.t, b.bl, b.bf, b.br); const g = c.hf * 1.32; raw.push(iso(c.x - g, c.y - g, 0), iso(c.x + g, c.y + g, 0)); });
    const f = fit(raw, [68, 66, 932, 934]), tx = f.tx;
    const paths = [], dots = [], ell = [];
    const sub = (p, o) => [p[0] - o[0], p[1] - o[1]];
    const mp = (o, U, V, u, v) => [o[0] + u * U[0] + v * V[0], o[1] + u * U[1] + v * V[1]];
    cubes.forEach((c) => {
        const b = boxFn(c.x, c.y, c.hf, c.hf, c.H); const t = b.t, bl = b.bl, bf = b.bf, br = b.br;
        const g = c.hf * 1.32;
        const base = [iso(c.x - g, c.y - g, 0), iso(c.x + g, c.y - g, 0), iso(c.x + g, c.y + g, 0), iso(c.x - g, c.y + g, 0)].map(tx);
        paths.push({ d: P(base, true), style: Object.assign(cons(), { strokeDasharray: '1.5 7', animation: `iconGlow 8s var(--e) ${(c.phase - 12).toFixed(2)}s infinite` }) });
        const rr = 9;
        paths.push({ d: RP([t[3], t[2], bf, bl].map(tx), rr), style: Object.assign(secondary(), { fill: 'var(--bg)', fillOpacity: 1 }) });
        paths.push({ d: RP([t[2], t[1], br, bf].map(tx), rr), style: Object.assign(secondary(), { fill: 'var(--bg)', fillOpacity: 1 }) });
        paths.push({ d: RP(t.map(tx), rr), style: Object.assign(primary(), { fill: 'var(--bg)', fillOpacity: 1 }) });
        const oT = t[3], UT = sub(t[2], t[3]), VT = sub(t[0], t[3]);
        { const im = 0.12; const q = [mp(oT, UT, VT, im, im), mp(oT, UT, VT, 1 - im, im), mp(oT, UT, VT, 1 - im, 1 - im), mp(oT, UT, VT, im, 1 - im)].map(tx); paths.push({ d: P(q, true), style: Object.assign(cons(), { strokeDasharray: '1.5 6', strokeOpacity: .3 }) }); }
        const N = 4, cu = 0.30, cv = 0.66, sp = 0.052;
        for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) {
            const cpt = tx(mp(oT, UT, VT, cu + (i - (N - 1) / 2) * sp, cv + (j - (N - 1) / 2) * sp));
            const h = ((i * 7 + j * 13) % 10) / 10;
            dots.push({ cx: cpt[0].toFixed(1), cy: cpt[1].toFixed(1), r: 1.5, style: { fill: 'var(--active)', opacity: 0, animation: `blink 6s var(--e) ${(c.phase * 0.6 + h * 2.4 - 12).toFixed(2)}s infinite` } });
        }
    });
    return { vb: vbFrom(f, 28), paths, ellipses: ell, dots, defs: '' };
}

/* ── Fig 03 · Launch race ────────────────────────────────────────────────── */
function buildLaunchRace() {
    const primary = (w) => ({ fill: 'none', stroke: 'var(--line)', strokeOpacity: .64, strokeWidth: w || 'var(--sw)', strokeLinejoin: 'round', strokeLinecap: 'round' });

    const N = 5, W = 146, H = 356;
    const step = [70, -108];
    const g0 = [-40, 210];
    const gates = [];
    for (let i = 0; i < N; i++) gates.push({ cx: g0[0] + step[0] * i, cy: g0[1] + step[1] * i });
    const pl = Math.hypot(step[0], step[1]); const pdir = [-step[1] / pl, step[0] / pl];
    const corners = (g) => {
        const lx = g.cx - pdir[0] * W, ly = g.cy - pdir[1] * W, rx = g.cx + pdir[0] * W, ry = g.cy + pdir[1] * W;
        return { BL: iso(lx, ly, 0), BR: iso(rx, ry, 0), TL: iso(lx, ly, H), TR: iso(rx, ry, H) };
    };
    const gMid = gates.map((g) => iso(g.cx, g.cy, H * 0.5));
    const start = iso(g0[0] - step[0] * 0.92, g0[1] - step[1] * 0.92, H * 0.42);
    const tGnd = [gates[N - 1].cx + step[0] * 1.0, gates[N - 1].cy + step[1] * 1.0];
    const target = iso(tGnd[0], tGnd[1], H * 0.6);
    const raw = [start, target];
    gates.forEach((g) => { const c = corners(g); raw.push(c.BL, c.BR, c.TL, c.TR); });
    const f = fit(raw, [60, 88, 940, 922]), tx = f.tx;

    const paths = [], dots = [], ell = [];
    const St = tx(start), TT = tx(target);

    const p0 = tx(iso(gates[0].cx, gates[0].cy, H * 0.5)), pN = tx(iso(gates[N - 1].cx, gates[N - 1].cy, H * 0.5));
    let dd = [pN[0] - p0[0], pN[1] - p0[1]]; const dl = Math.hypot(dd[0], dd[1]); dd = [dd[0] / dl, dd[1] / dl];
    const perp = [-dd[1], dd[0]], mid = [(St[0] + TT[0]) / 2, (St[1] + TT[1]) / 2];
    const trails = [[-58, 'none', 0], [0, '1 7', .3], [62, '9 9', .6]];
    trails.forEach(([off, dash, delay]) => {
        const bpt = [mid[0] + perp[0] * off, mid[1] + perp[1] * off];
        const a = [bpt[0] - dd[0] * 560, bpt[1] - dd[1] * 560], b = [bpt[0] + dd[0] * 560, bpt[1] + dd[1] * 560];
        paths.push({ d: P([a, b]), style: { fill: 'none', stroke: 'var(--active)', strokeWidth: 1.6, strokeDasharray: dash === 'none' ? undefined : dash, strokeLinecap: 'round', opacity: 0, animation: `trailFade 6s var(--e) ${delay}s infinite${dash === 'none' ? '' : ', trailFlow 5s linear infinite'}` } });
    });

    const passPct = 0.48 * 6;
    const T = [1.4, 1.88, 2.28, 2.60, 2.85];
    gates.forEach((g, i) => {
        const c = corners(g);
        const BL = tx(c.BL), BR = tx(c.BR), TL = tx(c.TL), TR = tx(c.TR);
        paths.push({ d: P([TL, TR]), style: primary() });
        paths.push({ d: P([BL, BR]), style: primary() });
        let U = [BR[0] - BL[0], BR[1] - BL[1]]; const ul = Math.hypot(U[0], U[1]); U = [U[0] / ul, U[1] / ul];
        const mag = 3.6, d_i = (T[i] - passPct).toFixed(2);
        paths.push({ d: P([BL, TL]), style: Object.assign(primary(), { '--ix': (U[0] * mag).toFixed(2) + 'px', '--iy': (U[1] * mag).toFixed(2) + 'px', animation: `gateClose 6s var(--e) ${d_i}s infinite` }) });
        paths.push({ d: P([BR, TR]), style: Object.assign(primary(), { '--ix': (-U[0] * mag).toFixed(2) + 'px', '--iy': (-U[1] * mag).toFixed(2) + 'px', animation: `gateClose 6s var(--e) ${d_i}s infinite` }) });
        const kf = i === N - 1 ? 'gateFlashLast' : 'gateFlash';
        paths.push({ d: P([BL, TL, TR, BR], true), style: { fill: 'none', stroke: 'var(--active)', strokeWidth: 3.4, strokeLinejoin: 'round', strokeLinecap: 'round', opacity: 0, filter: 'url(#glow4)', animation: `${kf} 6s var(--e) ${d_i}s infinite` } });
    });

    const rc = 26;
    ell.push({ cx: TT[0].toFixed(1), cy: TT[1].toFixed(1), rx: rc, ry: rc, style: { fill: 'none', stroke: 'var(--line)', strokeOpacity: .62, strokeWidth: 2.8, transformBox: 'fill-box', transformOrigin: '50% 50%', animation: 'targetPulse 6s var(--e) 0s infinite' } });
    const ring = 5, act = { 0: 'dotA', 1: 'dotB', 4: 'dotC' };
    for (let k = 0; k < ring; k++) {
        const ang = -Math.PI / 2 + k * (2 * Math.PI / ring);
        const x = TT[0] + Math.cos(ang) * rc, y = TT[1] + Math.sin(ang) * rc;
        if (act[k]) dots.push({ cx: x.toFixed(1), cy: y.toFixed(1), r: 2.6, style: { fill: 'var(--active)', filter: 'url(#glow4)', opacity: .78, animation: `${act[k]} 6s var(--e) 0s infinite` } });
        else dots.push({ cx: x.toFixed(1), cy: y.toFixed(1), r: 2.2, style: { fill: 'var(--sec)', opacity: .42 } });
    }
    dots.push({ cx: TT[0].toFixed(1), cy: TT[1].toFixed(1), r: 3.4, style: { fill: 'var(--active)', filter: 'url(#glow4)', opacity: .78, animation: 'centerOn 6s var(--e) 0s infinite' } });

    const vars = {};
    gMid.forEach((g, i) => { const p = tx(g); vars['--g' + (i + 1) + 'x'] = (p[0] - St[0]).toFixed(1) + 'px'; vars['--g' + (i + 1) + 'y'] = (p[1] - St[1]).toFixed(1) + 'px'; });
    vars['--tx'] = (TT[0] - St[0]).toFixed(1) + 'px'; vars['--ty'] = (TT[1] - St[1]).toFixed(1) + 'px';
    dots.push({ cx: St[0].toFixed(1), cy: St[1].toFixed(1), r: 5, style: Object.assign({ fill: 'var(--active)', filter: 'url(#glowB4)', opacity: 0 }, vars, { animation: 'signalRun 6s var(--e) 0s infinite' }) });

    return {
        vb: vbFrom(f, 64), paths, ellipses: ell, dots,
        defs: '<filter id="glow4" x="-300%" y="-300%" width="700%" height="700%"><feGaussianBlur stdDeviation="3.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter><filter id="glowB4" x="-400%" y="-400%" width="900%" height="900%"><feGaussianBlur stdDeviation="5.8" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>',
    };
}

/* ── rendering ───────────────────────────────────────────────────────────── */
function styleToString(style) {
    let out = '';
    for (const key in style) {
        const v = style[key];
        if (v === undefined || v === null) continue;
        const prop = key.charAt(0) === '-' ? key : key.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
        out += prop + ':' + v + ';';
    }
    return out;
}

function shapesToHTML(built) {
    let g = '';
    built.paths.forEach((p) => { g += `<path d="${p.d}" style="${styleToString(p.style)}"></path>`; });
    (built.ellipses || []).forEach((e) => { g += `<ellipse cx="${e.cx}" cy="${e.cy}" rx="${e.rx}" ry="${e.ry}" style="${styleToString(e.style)}"></ellipse>`; });
    built.dots.forEach((d) => { g += `<circle cx="${d.cx}" cy="${d.cy}" r="${d.r}" style="${styleToString(d.style)}"></circle>`; });
    return g;
}

function renderFigure(container, built) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', built.vb);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.innerHTML = (built.defs ? `<defs>${built.defs}</defs>` : '') +
        `<g style="animation:fig-reveal .9s var(--e) both">${shapesToHTML(built)}</g>`;
    container.innerHTML = '';
    container.appendChild(svg);
}

const BUILDERS = {
    delayed: buildDelayedSignal,
    scattered: buildScattered,
    launch: buildLaunchRace,
};

const FIGURES = {
    init() {
        document.querySelectorAll('.ao-prob-vis[data-fig]').forEach((el) => {
            const builder = BUILDERS[el.getAttribute('data-fig')];
            if (builder) renderFigure(el, builder());
        });
    },
};

export { FIGURES };
